import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Divider, Flex, Stack, Text, Toggle } from '@mollie/ui-react';

import { CATEGORIES, type CategoryId } from '../../types';
import { usePreferences } from '../../hooks/usePreferences';
import { pageMessages } from '../../messages';
import { PageLayout } from '../../components/PageLayout';
import { UnsubscribeAllModal } from './UnsubscribeAllModal';
import './ManagePreferences.css';

export function ManagePreferencesPage() {
  const intl = useIntl();
  const { preferences, userEmail, updatePreference, unsubscribeAll } = usePreferences();
  const [showUnsubAllModal, setShowUnsubAllModal] = useState(false);

  const handleToggle = (id: CategoryId, currentValue: boolean) => {
    updatePreference(id, !currentValue);
  };

  const handleConfirmUnsubAll = () => {
    unsubscribeAll();
    setShowUnsubAllModal(false);
  };

  return (
    <PageLayout>
      <Stack spacing="space-500">
        <Stack spacing="space-200">
          <Text className="pc-title">
            <FormattedMessage {...pageMessages.managePageTitle} />
          </Text>
          <Text variant="body-regular" color="secondary">
            <FormattedMessage {...pageMessages.managePageSubtitle} />
          </Text>
          <Text variant="caption-regular" color="secondary">
            <FormattedMessage
              {...pageMessages.managingFor}
              values={{ email: <strong>{userEmail}</strong> }}
            />
          </Text>
        </Stack>

        <Divider />

        <Stack spacing="space-400">
          {CATEGORIES.map((category, index) => (
            <Box key={category.id}>
              <Flex justifyContent="space-between" alignItems="center" gap="space-400">
                <Stack spacing="space-50">
                  <Text variant="body-medium">
                    <FormattedMessage {...category.name} />
                  </Text>
                  <Text variant="caption-regular" color="secondary">
                    <FormattedMessage {...category.description} />
                  </Text>
                </Stack>
                <Toggle
                  checked={preferences[category.id]}
                  onChange={() => handleToggle(category.id, preferences[category.id])}
                  aria-label={intl.formatMessage(category.name)}
                />
              </Flex>
              {index < CATEGORIES.length - 1 && (
                <Box marginBlockStart="space-400">
                  <Divider />
                </Box>
              )}
            </Box>
          ))}
        </Stack>

        <Divider />

        <Flex justifyContent="center">
          <button
            type="button"
            onClick={() => setShowUnsubAllModal(true)}
            className="pc-unsub-all-link"
          >
            <FormattedMessage {...pageMessages.unsubscribeAllButton} />
          </button>
        </Flex>
      </Stack>

      {showUnsubAllModal && (
        <UnsubscribeAllModal
          onConfirm={handleConfirmUnsubAll}
          onCancel={() => setShowUnsubAllModal(false)}
        />
      )}
    </PageLayout>
  );
}
